import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { GET_RESOURCES, GET_CATALOGS, CREATE_RESOURCE, UPDATE_RESOURCE, DELETE_RESOURCE, DELETE_RESOURCES } from '../graphql/queries';
import {
  Plus,
  Search,
  X,
  Edit2,
  Trash2,
  Folder,
  ChevronDown,
  ChevronRight,
  ArrowUp,
  ArrowDown
} from 'lucide-react';


import { Resource, Catalog } from '../types';

function Resources() {
  const [skillFilter, setSkillFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const toggleRow = (id: string) => {
    setExpandedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    skill: '',
    level: '',
    load: 0
  });

  const limit = 10;

  const { data, loading, refetch } = useQuery<{ resources: { resources: Resource[], totalPages: number, total: number } }>(GET_RESOURCES, {
    variables: {
      skill: skillFilter || undefined,
      level: levelFilter || undefined,
      search: searchQuery || undefined,
      page,
      limit,
      sortBy: sortBy || undefined
    }
  });

  const { data: skillsData } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, { variables: { type: 'resourceSkill' } });
  const { data: levelsData } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, { variables: { type: 'resourceLevel' } });
  const { data: statusesData } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, { variables: { type: 'projectStatus' } });

  const [createResource] = useMutation(CREATE_RESOURCE, {
    onCompleted: () => {
      closeModal();
      refetch();
    }
  });

  const [updateResource] = useMutation(UPDATE_RESOURCE, {
    onCompleted: () => {
      closeModal();
      refetch();
    }
  });

  const [deleteResource] = useMutation(DELETE_RESOURCE, {
    onCompleted: () => refetch()
  });

  const [deleteResources] = useMutation(DELETE_RESOURCES, {
    onCompleted: () => {
      setSelectedIds([]);
      refetch();
    }
  });

  const openCreateModal = () => {
    setEditingResource(null);
    setFormData({
      name: '',
      email: '',
      skill: '',
      level: '',
      load: 0
    });
    setShowModal(true);
  };

  const openEditModal = (resource: Resource) => {
    setEditingResource(resource);
    setFormData({
      name: resource.name,
      email: resource.email,
      skill: resource.skill || '',
      level: resource.level || '',
      load: resource.load ? parseFloat(String(resource.load)) : 0
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingResource(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const input = {
      ...formData,
      load: parseFloat(String(formData.load)) || 0
    };
    if (editingResource) {
      updateResource({ variables: { id: editingResource.id, input } });
    } else {
      createResource({ variables: { input } });
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      deleteResource({ variables: { id } });
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(resources.map(r => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
    }
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedIds.length} resources?`)) {
      deleteResources({ variables: { ids: selectedIds } });
    }
  };

  const getInitials = (name?: string) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const getAvatarColor = (name?: string) => {
    const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444'];
    const index = name?.charCodeAt(0) ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  const getLevelColor = (level: string) => {
    if (!level) return '#64748B';
    const found = levelsData?.catalogs?.find(
      c => c.name.toLowerCase() === level.toLowerCase()
    );
    return found?.color || '#64748B';
  };

  const getStatusColor = (status: string) => {
    if (!status) return '#64748B';
    const found = statusesData?.catalogs?.find(
      c => c.name.toLowerCase() === status.toLowerCase()
    );
    return found?.color || '#64748B';
  };

  const resources = data?.resources?.resources || [];
  const totalPages = data?.resources?.totalPages || 1;
  const total = data?.resources?.total || 0;


  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Resources</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          {selectedIds.length > 0 && (
            <button className="btn btn-danger" onClick={handleBulkDelete}>
              <Trash2 size={18} />
              Delete Selected ({selectedIds.length})
            </button>
          )}
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={18} />
            New Resource
          </button>
        </div>
      </div>

      <div className="page-content">
        <div className="filters-bar">
          <select
            className="filter-select"
            value={skillFilter}
            onChange={(e) => {
              setSkillFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Skills</option>
            {skillsData?.catalogs?.map(skill => (
              <option key={skill.id} value={skill.name}>{skill.name}</option>
            ))}
          </select>

          <select
            className="filter-select"
            value={levelFilter}
            onChange={(e) => {
              setLevelFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Levels</option>
            {levelsData?.catalogs?.map(level => (
              <option key={level.id} value={level.name}>{level.name}</option>
            ))}
          </select>

          <select
            className="filter-select"
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Sort by Name (A-Z)</option>
            <option value="name_desc">Sort by Name (Z-A)</option>
            <option value="load_desc">Load: High to Low</option>
            <option value="load_asc">Load: Low to High</option>
            <option value="projects_desc">Assigned Projects: More to Less</option>
            <option value="projects_asc">Assigned Projects: Less to More</option>
          </select>

          <div className="flex-spacer" />
 
          <div className="search-wrapper">
            <Search size={18} className="search-icon-left" />
            <input
              type="text"
              className="search-input search-input-with-icon"
              placeholder="Search by name or email"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={resources.length > 0 && selectedIds.length === resources.length}
                    ref={input => {
                      if (input) {
                        input.indeterminate = selectedIds.length > 0 && selectedIds.length < resources.length;
                      }
                    }}
                  />
                </th>
                <th
                  onClick={() => {
                    setSortBy(sortBy === '' || sortBy === 'name_asc' ? 'name_desc' : '');
                    setPage(1);
                  }}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Resource
                    {(sortBy === '' || sortBy === 'name_asc') ? <ArrowUp size={14} /> : sortBy === 'name_desc' ? <ArrowDown size={14} /> : null}
                  </div>
                </th>
                <th>Skill</th>
                <th>Level</th>
                <th
                  onClick={() => {
                    setSortBy(sortBy === 'load_desc' ? 'load_asc' : 'load_desc');
                    setPage(1);
                  }}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Load
                    {sortBy === 'load_desc' ? <ArrowDown size={14} /> : sortBy === 'load_asc' ? <ArrowUp size={14} /> : null}
                  </div>
                </th>
                <th
                  onClick={() => {
                    setSortBy(sortBy === 'projects_desc' ? 'projects_asc' : 'projects_desc');
                    setPage(1);
                  }}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Projects
                    {sortBy === 'projects_desc' ? <ArrowDown size={14} /> : sortBy === 'projects_asc' ? <ArrowUp size={14} /> : null}
                  </div>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {resources.map((resource) => {
                const isExpanded = expandedIds.includes(resource.id);
                return (
                  <React.Fragment key={resource.id}>
                    <tr style={{ background: isExpanded ? 'rgba(79, 70, 229, 0.01)' : 'transparent' }}>
                      <td style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(resource.id)}
                          onChange={(e) => handleSelectOne(resource.id, e.target.checked)}
                        />
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            className="btn-icon"
                            onClick={() => toggleRow(resource.id)}
                            style={{ 
                              padding: '4px', 
                              cursor: 'pointer', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              background: 'transparent',
                              border: 'none',
                              color: 'var(--text-secondary)'
                            }}
                          >
                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                          <div
                            className="avatar avatar-md"
                            style={{ background: getAvatarColor(resource.name) }}
                          >
                            {getInitials(resource.name)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 500 }}>{resource.name}</div>
                            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{resource.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>{resource.skill || '-'}</td>
                      <td>
                        <span 
                          className="badge"
                          style={{
                            backgroundColor: `${getLevelColor(resource.level)}15`,
                            color: getLevelColor(resource.level),
                            border: `1px solid ${getLevelColor(resource.level)}25`
                          }}
                        >
                          {resource.level || '-'}
                        </span>
                      </td>
                      <td>
                        {(() => {
                          const load = resource.load !== undefined ? parseFloat(String(resource.load)) : 0;
                          if (load > 100) {
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div className="progress-bar" style={{ width: '60px' }}>
                                  <div
                                    className="progress-fill"
                                    style={{ width: '100%', background: 'var(--danger)' }}
                                  />
                                </div>
                                <span style={{ fontSize: '13px', color: 'var(--danger)', fontWeight: 600 }}>
                                  {load}% Carga (Sobrecargado)
                                </span>
                              </div>
                            );
                          } else {
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div className="progress-bar" style={{ width: '60px' }}>
                                  <div
                                    className="progress-fill"
                                    style={{
                                      width: `${load}%`,
                                      background: load > 70 ? 'var(--warning)' : 'var(--success)'
                                    }}
                                  />
                                </div>
                                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{load}% ocupado</span>
                              </div>
                            );
                          }
                        })()}
                      </td>
                      <td onClick={() => toggleRow(resource.id)} style={{ cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500, color: 'var(--primary)' }}>
                          <Folder size={14} style={{ color: 'var(--primary)' }} />
                          {resource.projectCount || 0}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button className="btn-icon" onClick={() => openEditModal(resource)}>
                            <Edit2 size={16} />
                          </button>
                          <button className="btn-icon" onClick={() => handleDelete(resource.id)}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr style={{ background: 'rgba(79, 70, 229, 0.01)' }}>
                        <td colSpan={7} style={{ padding: '16px 24px 24px 72px', borderBottom: '1px solid var(--border)' }}>
                          <div style={{ border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--surface)', padding: '16px', boxShadow: 'var(--shadow-sm)' }}>
                            <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Folder size={16} style={{ color: 'var(--primary)' }} />
                              Proyectos Asignados ({resource.projects?.length || 0})
                            </h4>
                            
                            {resource.projects && resource.projects.length > 0 ? (
                              <div className="table-container" style={{ border: 'none', margin: 0, boxShadow: 'none', background: 'transparent' }}>
                                <table className="table" style={{ border: 'none', minWidth: '100%' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ background: 'transparent', padding: '6px 12px', fontSize: '11px', color: 'var(--text-muted)' }}>Proyecto</th>
                                      <th style={{ background: 'transparent', padding: '6px 12px', fontSize: '11px', color: 'var(--text-muted)' }}>Rol</th>
                                      <th style={{ background: 'transparent', padding: '6px 12px', fontSize: '11px', color: 'var(--text-muted)' }}>Estado</th>
                                      <th style={{ background: 'transparent', padding: '6px 12px', fontSize: '11px', color: 'var(--text-muted)' }}>Prioridad</th>
                                      <th style={{ background: 'transparent', padding: '6px 12px', fontSize: '11px', color: 'var(--text-muted)' }}>Progreso</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {resource.projects.map((proj) => {
                                      const devInfo = proj.developers?.find(d => d?.resource?.id === resource.id);
                                      const role = devInfo?.role === 'lead' ? 'Líder' : 'Soporte';
                                      


                                      const getPriorityClass = (priority: string) => {
                                        const map: Record<string, string> = {
                                          'High': 'badge-danger',
                                          'Medium': 'badge-warning',
                                          'Low': 'badge-primary'
                                        };
                                        return map[priority] || 'badge-primary';
                                      };

                                      return (
                                        <tr key={proj.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                          <td style={{ padding: '6px 12px' }}>
                                            <Link 
                                              to={`/projects/${proj.id}`} 
                                              style={{ fontWeight: 500, color: 'var(--primary)', textDecoration: 'none' }}
                                            >
                                              {proj.name}
                                            </Link>
                                          </td>
                                          <td style={{ padding: '6px 12px', fontSize: '12px' }}>
                                            <span className={`badge ${devInfo?.role === 'lead' ? 'badge-active' : 'badge-planned'}`}>
                                              {role}
                                            </span>
                                          </td>
                                          <td style={{ padding: '6px 12px', fontSize: '12px' }}>
                                            <span 
                                              className="badge"
                                              style={{
                                                backgroundColor: `${getStatusColor(proj.status)}15`,
                                                color: getStatusColor(proj.status),
                                                border: `1px solid ${getStatusColor(proj.status)}25`
                                              }}
                                            >
                                              {proj.status}
                                            </span>
                                          </td>
                                          <td style={{ padding: '6px 12px', fontSize: '12px' }}>
                                            <span className={`badge ${getPriorityClass(proj.priority || 'Medium')}`}>
                                              {proj.priority || 'Medium'}
                                            </span>
                                          </td>
                                          <td style={{ padding: '6px 12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                              <div className="progress-bar" style={{ width: '50px' }}>
                                                <div
                                                  className="progress-fill"
                                                  style={{ width: `${proj.progress || 0}%`, background: 'var(--success)' }}
                                                />
                                              </div>
                                              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                                {proj.progress || 0}%
                                              </span>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0, padding: '4px 0' }}>
                                Este recurso no tiene proyectos asignados actualmente.
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {resources.length === 0 && !loading && (
            <div className="empty-state">
              <h3>No resources found</h3>
              <p>Add your first team member to get started</p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Page {page} of {totalPages} ({total} total)
              </div>
              <div className="pagination-buttons">
                <button
                  className="pagination-btn"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    className={`pagination-btn ${p === page ? 'active' : ''}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  className="pagination-btn"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingResource ? 'Edit Resource' : 'New Resource'}</h2>
              <button className="btn-icon" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Skill</label>
                    <select
                      className="form-select"
                      value={formData.skill}
                      onChange={(e) => setFormData({ ...formData, skill: e.target.value })}
                    >
                      <option value="">Select skill</option>
                      {skillsData?.catalogs?.map((skill: Catalog) => (
                        <option key={skill.id} value={skill.name}>{skill.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Level</label>
                    <select
                      className="form-select"
                      value={formData.level}
                      onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    >
                      <option value="">Select level</option>
                      {levelsData?.catalogs?.map(level => (
                        <option key={level.id} value={level.name}>{level.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Load (%)</label>
                  <input
                    type="number"
                    className="form-input"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.load}
                    onChange={(e) => setFormData({ ...formData, load: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingResource ? 'Save Changes' : 'Create Resource'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Resources;
